import apiFetch from "@/app/auth/apiFetch";
import { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export type PluginLogSeverity = "error" | "warning" | "transient";

/** List row — lightweight (stack/context only via the detail endpoint). */
export interface PluginErrorLog {
  id: number;
  worker_id?: string | null;
  subscription_id?: number | null;
  user_id?: string | null;
  source: string;
  reason: string;
  severity: string;
  message?: string | null;
  consecutive_failures?: number | null;
  plugin_version?: string | null;
  /** epoch ms — plugin-side timestamp of the error (Date.now()). */
  error_ts?: number | null;
  created_at?: string | null;
}

/** Full row — adds the potentially-large stack trace + JSON context. */
export interface PluginErrorLogDetail extends PluginErrorLog {
  stack?: string | null;
  context?: Record<string, unknown> | null;
}

export interface PaginatedPluginLogs {
  items: PluginErrorLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface PluginLogsStatsBucket {
  key: string;
  count: number;
}

export interface PluginLogsStats {
  days: number;
  total: number;
  by_severity: PluginLogsStatsBucket[];
  by_reason: PluginLogsStatsBucket[];
  by_source: PluginLogsStatsBucket[];
}

export interface ListPluginLogsParams {
  limit?: number;
  offset?: number;
  worker_id?: string;
  source?: string;
  reason?: string;
  severity?: string;
  /** ISO 8601 — created_at >= */
  since?: string;
  /** ISO 8601 — created_at <= */
  until?: string;
  /** Free-text ILIKE on message/reason/source. */
  q?: string;
}

export async function listPluginLogs(
  navigate: NavigateFunction,
  params?: ListPluginLogsParams,
): Promise<PaginatedPluginLogs> {
  const p = new URLSearchParams();
  if (params?.limit != null) p.set("limit", String(params.limit));
  if (params?.offset != null) p.set("offset", String(params.offset));
  if (params?.worker_id) p.set("worker_id", params.worker_id);
  if (params?.source) p.set("source", params.source);
  if (params?.reason) p.set("reason", params.reason);
  if (params?.severity) p.set("severity", params.severity);
  if (params?.since) p.set("since", params.since);
  if (params?.until) p.set("until", params.until);
  if (params?.q) p.set("q", params.q);
  const qs = p.toString();
  const res = await apiFetch(
    `${API_URL}/plugin-logs${qs ? `?${qs}` : ""}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement des logs plugins");
  return res.json();
}

export async function getPluginLog(
  navigate: NavigateFunction,
  id: number,
): Promise<PluginErrorLogDetail> {
  const res = await apiFetch(
    `${API_URL}/plugin-logs/${id}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement du log plugin");
  return res.json();
}

export async function getPluginLogsStats(
  navigate: NavigateFunction,
  days = 7,
): Promise<PluginLogsStats> {
  const res = await apiFetch(
    `${API_URL}/plugin-logs/stats?days=${days}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok)
    throw new Error("Erreur lors du chargement des statistiques des logs plugins");
  return res.json();
}
